import ApiService from "../../../../services/ApiService";
import { formatToISODateTime } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import { CursusStage } from "../../Course.d";
import { ComplianceForm, CursusStageForm } from "./EditCursusStagePopup.d";

export const cursusStageToForm = (course: CursusStage): CursusStageForm => ({
  content: course.content || "",
  cursus_prefix: course.cursus_prefix,
  default_cadre: course.default_cadre,
  deployment_name: course.deployment_name,
  dt_end: formatToISODateTime(course.dt_end).slice(0, 10),
  dt_start: formatToISODateTime(course.dt_start).slice(0, 10),
  end_of_formation_document: course.end_of_formation_document || "",
  evaluations: course.evaluations || "",
  format: course.format,
  hours_spent_on_masterclass: course.hours_spent_on_masterclass,
  hours_spent_on_modules: course.hours_spent_on_modules,
  hours_spent_on_project: course.hours_spent_on_project,
  is_disabled: course.is_disabled,
  language: course.language,
  mediator: course.mediator || "",
  objectives: course.objectives || "",
  pedagogy_leader_diplomas: course.pedagogy_leader_diplomas || "",
  pedagogy_leader_references: course.pedagogy_leader_references || "",
  prerequisites: course.prerequisites || "",
  presentation_document_url: course.presentation_document_url || "",
  priority: course.priority,
  total_hours: course.total_hours,
  training_organization: course.training_organization || "",
});

export const getChangedFields = (currentValues: any, initialValues: any) =>
  Object.fromEntries(
    Object.entries(currentValues).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(initialValues[key]))
  );

export const updateCursusStage = (
  cursusStageId: number,
  data: object,
  onSuccess: Function,
  setIsSaving: Function,
  language: string
) => {
  ApiService.patch(`cursus/stages/${cursusStageId}`, data)
    .then(() => onSuccess())
    .catch(() => {
      setIsSaving(false);
      alert(translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.SAVE_ERROR"));
    });
};

export const saveComplianceFields = (
  cursusStageId: number,
  data: object | null,
  onSuccess: Function,
  setIsSaving: Function,
  language: string
) => {
  ApiService.put(`cursus/stages/${cursusStageId}/external-compliances`, data)
    .then(() => onSuccess())
    .catch(() => {
      setIsSaving(false);
      alert(translate(language, "PAGES.COURSE.MODALS.EDIT_STAGE.CERTIFIER.SAVE_ERROR"));
    });
};

export const complianceToForm = (compliance: CursusStage["external_compliance"]): ComplianceForm | null => {
  if (!compliance) return null;
  const { id, cursus_stage, dt_added, dt_updated, skill_blocks, ...fields } = compliance;
  return {
    ...fields,
    skill_blocks: [...skill_blocks]
      .sort((a, b) => a.order - b.order)
      .map(({ id, name, content, is_mandatory }, order) => ({ id, name, content, is_mandatory, order })),
  };
};

export const isComplianceValid = (form: ComplianceForm | null) => {
  if (!form) return true;
  return (
    ["rncp", "rs", "azav"].includes(form.type) &&
    [form.name, form.code, form.certifier_name].every((value) => value.trim() !== "" && value.length <= 255) &&
    [form.internship_duration_days, form.internship_duration_hours, form.end_of_study_duration].every(
      (value) => value === null || (Number.isInteger(value) && value >= 0)
    ) &&
    form.skill_blocks.every((block) => block.name.trim() !== "" && block.name.length <= 255)
  );
};

export const isCursusStageFormValid = (
  form: CursusStageForm,
  areDatesValid: boolean,
  compliance: ComplianceForm | null
) =>
  form.deployment_name.trim() !== "" &&
  form.cursus_prefix.trim() !== "" &&
  areDatesValid &&
  form.language !== "" &&
  isComplianceValid(compliance);
